import { useDisclosure } from '@chakra-ui/react';
import { useAnalytics } from 'hooks/useAnalytics';
import { useGlobals } from 'hooks/useGlobals';
import { useIsSkincare } from 'hooks/useIsSkincare';
import { PARTNERS, PROMOTIONS } from 'lib/constants/routes';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { useEffectOnce } from 'react-use';

import { Popup } from './Popup';
import { useViewedVariants } from './useViewedVariants';

const DIALOG_RECHECK_DELAY = 1000;

export const FullscreenPopup = () => {
  const pathname = usePathname();
  const isSkincare = useIsSkincare();

  const { analytics } = useAnalytics();
  const [hasPopupBeenViewed, setHasPopupBeenViewed] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const { data: globals } = useGlobals();
  const { fullscreenPopup } = globals || {};
  const variant = fullscreenPopup?.variant;
  const popupDelay = (fullscreenPopup?.delay || 0) * 1000;

  const {
    isOpen: isModalOpen,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure();

  const isExcludedPage =
    pathname?.includes(PARTNERS) || pathname?.includes(PROMOTIONS);

  const { shouldShowVariant, getHasViewedVariant, addViewedVariant } =
    useViewedVariants();

  const shouldShowPopup =
    isMounted &&
    !!globals &&
    !hasPopupBeenViewed &&
    !isExcludedPage &&
    fullscreenPopup?.isPopupEnabled &&
    shouldShowVariant(variant);

  const showPopup = useCallback(() => {
    if (isModalOpen) return;
    onOpenModal();
    analytics.overlayPopupDisplay();
  }, [onOpenModal, analytics, isModalOpen]);

  const onClose = (action?: 'click' | 'close', url?: string) => {
    onCloseModal();

    if (action === 'click') {
      analytics.overlayPopupClick({ url: url ?? '' });
    }

    if (action === 'close') {
      analytics.overlayPopupClose();
    }

    setHasPopupBeenViewed(true);
    addViewedVariant(variant);
  };

  useEffectOnce(() => {
    setIsMounted(true);
  });

  useEffect(() => {
    onCloseModal();
    setHasPopupBeenViewed(getHasViewedVariant(variant));
  }, [isSkincare, variant, getHasViewedVariant, onCloseModal]);

  useEffect(() => {
    if (!shouldShowPopup) return;

    let timeout: ReturnType<typeof setTimeout>;

    const showPopupOnceNoOtherDialogIsOpen = () => {
      // Opening over another dialog steals its focus, which leaves Escape
      // closing this popup instead of what the visitor was actually using.
      if (document.querySelector('[role="dialog"]')) {
        timeout = setTimeout(
          showPopupOnceNoOtherDialogIsOpen,
          DIALOG_RECHECK_DELAY,
        );
        return;
      }
      showPopup();
    };

    timeout = setTimeout(showPopupOnceNoOtherDialogIsOpen, popupDelay);

    return () => clearTimeout(timeout);
  }, [shouldShowPopup, showPopup, popupDelay]);

  if (!shouldShowPopup) return null;

  return <Popup {...fullscreenPopup} isOpen={isModalOpen} onClose={onClose} />;
};
